import { Figure, svgType } from './Figure'

interface Node {
  id: string
  label: string
  x: number
  y: number
  colour: string
}

interface Edge {
  from: string
  to: string
  /** Stroke width in px. Proportional to the dollars that moved. */
  weight: number
  colour: string
}

const W = 140
const H = 30

const NODES: Node[] = [
  { id: 'salary', label: 'Income: Salary', x: 0, y: 42, colour: 'var(--inflow)' },
  { id: 'interest', label: 'Income: Interest', x: 0, y: 132, colour: 'var(--inflow)' },

  { id: 'chequing', label: 'Chequing', x: 290, y: 42, colour: 'var(--text)' },
  { id: 'savings', label: 'Savings', x: 290, y: 132, colour: 'var(--text)' },

  { id: 'housing', label: 'Expense: Housing', x: 580, y: 8, colour: 'var(--outflow)' },
  { id: 'groceries', label: 'Expense: Food', x: 580, y: 78, colour: 'var(--outflow)' },
  { id: 'tfsa', label: 'TFSA', x: 580, y: 148, colour: 'var(--invest)' },
]

const EDGES: Edge[] = [
  { from: 'salary', to: 'chequing', weight: 11, colour: 'var(--inflow)' },
  { from: 'interest', to: 'savings', weight: 2, colour: 'var(--inflow)' },
  { from: 'chequing', to: 'housing', weight: 7, colour: 'var(--outflow)' },
  { from: 'chequing', to: 'groceries', weight: 3.5, colour: 'var(--outflow)' },
  { from: 'chequing', to: 'savings', weight: 2.5, colour: 'var(--text-faint)' },
  { from: 'savings', to: 'tfsa', weight: 4, colour: 'var(--invest)' },
]

const byId = (id: string) => NODES.find((n) => n.id === id)!

/**
 * The money-flow map, reduced to the parts that matter conceptually: accounts
 * are nodes, and an edge exists only where one journal entry touched both
 * accounts. Edge width is dollars, so nothing on this canvas is estimated.
 */
export function FlowMapDiagram() {
  return (
    <Figure
      title="A money flow map: salary into chequing, chequing out to housing, food and savings, savings into a TFSA."
      caption="Every edge is a pair of ledger lines from the same journal entry. There is no heuristic guessing that a withdrawal and a deposit were the same movement — the entry already says so."
    >
      <svg viewBox="0 0 720 250" xmlns="http://www.w3.org/2000/svg">
        {/* Edges first so nodes sit on top of the joins. */}
        <g fill="none" strokeLinecap="round">
          {EDGES.map((edge) => {
            const a = byId(edge.from)
            const b = byId(edge.to)
            const x1 = a.x + W
            const y1 = a.y + H / 2
            const x2 = b.x
            const y2 = b.y + H / 2
            const mid = (x1 + x2) / 2
            // Same-column hop (chequing -> savings) needs to bow out rather
            // than cut straight through the node stack.
            const d = a.x === b.x
              ? `M${x1} ${y1} C ${x1 + 60} ${y1}, ${x1 + 60} ${y2}, ${x1} ${y2}`
              : `M${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`
            return (
              <path
                key={`${edge.from}-${edge.to}`}
                d={d}
                stroke={edge.colour}
                strokeWidth={edge.weight}
                opacity="0.42"
              />
            )
          })}
        </g>

        {NODES.map((node) => (
          <g key={node.id}>
            <rect
              x={node.x} y={node.y} width={W} height={H} rx="4"
              fill="var(--glass-2)" stroke={node.colour} strokeWidth="1" opacity="0.95"
            />
            <text x={node.x + 12} y={node.y + 20} {...svgType.faint} fill="var(--text)">
              {node.label}
            </text>
          </g>
        ))}

        <line x1="0" y1="212" x2="720" y2="212" stroke="var(--border)" strokeWidth="1" />
        <text x="0" y="234" {...svgType.faint}>
          Node colour follows account type. Edge width follows the amount. Both are read from the ledger, not inferred.
        </text>
      </svg>
    </Figure>
  )
}
